/**
 * Semantic Color Tokens (Internal Design System)
 *
 * Maps primitive colors to semantic meanings for light and dark themes.
 * All values reference colors from ./colors.ts
 */
import { alpha } from '@mui/material/styles'

import { colors } from './colors'

export const lightTokens = {
  text: {
    primary: colors.neutral[900],
    secondary: colors.neutral[700],
    disabled: colors.neutral[500],
    states: {
      hover: alpha(colors.neutral[900], colors.opacity.hover),
      selected: alpha(colors.neutral[900], colors.opacity.selected),
      focus: alpha(colors.neutral[900], colors.opacity.focus),
      outlinedBorder: colors.neutral[200],
    },
  },
  primary: {
    main: colors.primary[600],
    dark: colors.primary[900],
    light: colors.primary[100],
    lighter: colors.primary[50],
    contrastText: colors.neutral[0],
    states: {
      hover: alpha(colors.primary[700], colors.opacity.hover),
      selected: alpha(colors.primary[700], colors.opacity.selected),
      focus: alpha(colors.primary[700], colors.opacity.focus),
      outlinedBorder: colors.neutral[200],
    },
  },
  secondary: {
    main: colors.secondary[600],
    dark: colors.secondary[900],
    light: colors.secondary[100],
    lighter: colors.secondary[50],
    states: {
      hover: alpha(colors.secondary[700], colors.opacity.hover),
      selected: alpha(colors.secondary[700], colors.opacity.selected),
      focus: alpha(colors.secondary[700], colors.opacity.focus),
      focusVisible: alpha(colors.secondary[700], colors.opacity.focusVisible),
      outlinedBorder: colors.neutral[200],
    },
  },
  action: {
    active: alpha(colors.neutral[900], colors.opacity.active),
    hover: alpha(colors.neutral[900], colors.opacity.hover),
    selected: alpha(colors.neutral[900], colors.opacity.selected),
    focus: alpha(colors.neutral[900], colors.opacity.focus),
    disabled: alpha(colors.neutral[900], colors.opacity.disabled),
    disabledBackground: alpha(colors.neutral[900], colors.opacity.disabledBackground),
  },
  error: {
    main: colors.red[600],
    dark: colors.red[800],
    light: colors.red[100],
    lighter: colors.red[50],
  },
  warning: {
    main: colors.orange[600],
    dark: colors.orange[800],
    light: colors.orange[100],
    lighter: colors.orange[50],
  },
  info: {
    main: colors.blue[600],
    dark: colors.blue[800],
    light: colors.blue[100],
    lighter: colors.blue[50],
  },
  success: {
    main: colors.green[600],
    dark: colors.green[800],
    light: colors.green[100],
    lighter: colors.green[50],
  },
  testMode: {
    main: colors.teal[600],
    dark: colors.teal[800],
    light: colors.teal[100],
    lighter: colors.teal[50],
  },
  aiRecommendation: {
    main: colors.indigo[600],
    dark: colors.indigo[800],
    light: colors.indigo[100],
    lighter: colors.indigo[50],
    background: colors.indigo[50],
    // Tinted surface behind AI-generated summaries; SCSS renders it as a gradient.
    summaryBg: '#EAE9FC',
  },
  aiSurface: {
    assistant: { bg: colors.indigo[50], fg: colors.indigo[500] },
    chat: { bg: colors.blue[50], fg: colors.blue[500] },
    email: { bg: colors.orange[50], fg: colors.orange[500] },
    search: { bg: colors.teal[50], fg: colors.teal[500] },
    sms: { bg: colors.pink[50], fg: colors.pink[500] },
    voice: { bg: colors.purple[50], fg: colors.purple[500] },
  },
  upgrade: {
    main: colors.indigo[600],
    dark: colors.indigo[800],
    light: colors.indigo[100],
    lighter: colors.indigo[50],
  },
  feedback: {
    main: colors.pink[600],
    dark: colors.pink[800],
    light: colors.pink[100],
    lighter: colors.pink[50],
  },
  common: {
    transparent: 'transparent',
    blackStates: {
      main: colors.neutral[900],
      hover: alpha(colors.neutral[900], colors.opacity.hover),
      selected: alpha(colors.neutral[900], colors.opacity.selected),
      focus: alpha(colors.neutral[900], colors.opacity.focus),
      focusVisible: alpha(colors.neutral[900], colors.opacity.focusVisible),
      outlinedBorder: alpha(colors.neutral[900], colors.opacity.outlinedBorder),
    },
    whiteStates: {
      main: colors.neutral[0],
      hover: alpha(colors.neutral[0], colors.opacity.hover),
      selected: alpha(colors.neutral[0], colors.opacity.selected),
      focus: alpha(colors.neutral[0], colors.opacity.focus),
      focusVisible: alpha(colors.neutral[0], colors.opacity.focusVisible),
      outlinedBorder: alpha(colors.neutral[0], colors.opacity.outlinedBorder),
    },
  },
  background: {
    default: colors.neutral[0],
    background1: colors.neutral[0],
    background2: colors.neutral[50],
    background3: colors.neutral[100],
    tooltip: colors.neutral[900],
    gradientPage: 'linear-gradient(135deg, rgb(226, 235, 245) 0%, rgb(247, 237, 243) 100%)',
  },
  border: colors.neutral[200],
  divider: colors.neutral[200],
  native: {
    scrollbarBg: colors.neutral[200],
  },
  focusRing: colors.focus[400],
  input: {
    background: colors.neutral[0],
    disabledBackground: colors.neutral[100],
    border: colors.neutral[300],
    hoverBorder: colors.primary[700],
    focusBorder: colors.primary[700],
    focusRing: colors.primary[100],
    placeholder: colors.neutral[500],
    // Aliases matching legacy theme palette.input keys so shared components
    // (e.g. RichTextEditor) can build shadows from theme.palette.input.*
    default: colors.neutral[300],
    hover: colors.primary[700],
    focus: colors.primary[100],
  },
  switch: {
    thumb: colors.neutral[50],
    thumbDisabled: colors.neutral[100],
    track: colors.neutral[300],
  },
  slider: {
    valueLabelBackground: colors.neutral[800],
    valueLabelText: colors.neutral[0],
  },
  stepper: {
    connector: colors.neutral[200],
  },
  processFlow: {
    border: {
      hover: colors.neutral[800],
      applicant: colors.coral[500],
      applicantHover: colors.coral[700],
      internal: colors.amber[600],
      internalHover: colors.amber[700],
      bill: colors.green[400],
      billHover: colors.green[600],
      communication: colors.purple[600],
      communicationHover: colors.purple[700],
      error: alpha(colors.red[100], 0.3),
      default: colors.neutral[400],
    },
    background: {
      dark: colors.neutral[600],
      applicant: colors.coral[100],
      internal: colors.amber[100],
      approved: colors.green[50],
    },
  },
  actionColors: {
    blue: colors.blue[400],
    teal: colors.teal[500],
    purple: colors.purple[400],
    pink: colors.pink[400],
  },
  // Accent colors exposed for direct use
  amber: { main: colors.amber[600] },
  purple: { main: colors.purple[600] },
  teal: { main: colors.teal[600] },
  indigo: { main: colors.indigo[600] },
  lime: { main: colors.lime[600] },
  pink: { main: colors.pink[600] },
  deepPurple: { main: colors.deepPurple[600] },
  neutral: { main: colors.neutral[500] },
}

export const darkTokens = {
  text: {
    primary: colors.neutral[0],
    secondary: colors.neutral[400],
    disabled: colors.neutral[800],
    states: {
      hover: alpha(colors.neutral[0], colors.opacity.hover),
      selected: alpha(colors.neutral[0], colors.opacity.selected),
      focus: alpha(colors.neutral[0], colors.opacity.focus),
      outlinedBorder: colors.neutral[600],
    },
  },
  primary: {
    main: colors.primary[500],
    dark: colors.primary[700],
    light: colors.primary[800],
    lighter: colors.primary[900],
    contrastText: colors.neutral[0],
    states: {
      hover: alpha(colors.primary[500], colors.opacity.hover),
      selected: alpha(colors.primary[500], colors.opacity.selected),
      focus: alpha(colors.primary[500], colors.opacity.focus),
      outlinedBorder: colors.neutral[600],
    },
  },
  secondary: {
    main: colors.secondary[500],
    dark: colors.secondary[700],
    light: colors.secondary[800],
    lighter: colors.secondary[900],
    states: {
      hover: alpha(colors.secondary[500], colors.opacity.hover),
      selected: alpha(colors.secondary[500], colors.opacity.selected),
      focus: alpha(colors.secondary[500], colors.opacity.focus),
      focusVisible: alpha(colors.secondary[500], colors.opacity.focusVisible),
      outlinedBorder: colors.neutral[600],
    },
  },
  action: {
    active: alpha(colors.neutral[0], colors.opacity.active),
    hover: alpha(colors.neutral[0], colors.opacity.hover),
    selected: alpha(colors.neutral[0], colors.opacity.selected),
    focus: alpha(colors.neutral[0], colors.opacity.focus),
    disabled: alpha(colors.neutral[0], colors.opacity.disabled),
    disabledBackground: alpha(colors.neutral[0], colors.opacity.disabledBackground),
  },
  error: {
    main: colors.red[500],
    dark: colors.red[700],
    light: colors.red[900],
    lighter: colors.red[900],
  },
  warning: {
    main: colors.orange[500],
    dark: colors.orange[700],
    light: colors.orange[900],
    lighter: colors.orange[900],
  },
  info: {
    main: colors.blue[500],
    dark: colors.blue[700],
    light: colors.blue[900],
    lighter: colors.blue[900],
  },
  success: {
    main: colors.green[500],
    dark: colors.green[700],
    light: colors.green[900],
    lighter: colors.green[900],
  },
  testMode: {
    main: colors.teal[500],
    dark: colors.teal[700],
    light: colors.teal[900],
    lighter: colors.teal[900],
  },
  aiRecommendation: {
    main: colors.deepPurple[500],
    dark: colors.deepPurple[700],
    light: colors.deepPurple[900],
    lighter: colors.deepPurple[900],
  },
  aiSurface: {
    assistant: { bg: colors.indigo[900], fg: colors.indigo[300] },
    chat: { bg: colors.blue[900], fg: colors.blue[300] },
    email: { bg: colors.orange[900], fg: colors.orange[300] },
    search: { bg: colors.teal[900], fg: colors.teal[300] },
    sms: { bg: colors.pink[900], fg: colors.pink[300] },
    voice: { bg: colors.purple[900], fg: colors.purple[300] },
  },
  upgrade: {
    main: colors.indigo[500],
    dark: colors.indigo[700],
    light: colors.indigo[900],
    lighter: colors.indigo[900],
  },
  feedback: {
    main: colors.pink[500],
    dark: colors.pink[700],
    light: colors.pink[900],
    lighter: colors.pink[900],
  },
  common: {
    transparent: 'transparent',
    blackStates: {
      main: colors.neutral[0],
      hover: alpha(colors.neutral[0], colors.opacity.hover),
      selected: alpha(colors.neutral[0], colors.opacity.selected),
      focus: alpha(colors.neutral[0], colors.opacity.focus),
      focusVisible: alpha(colors.neutral[0], colors.opacity.focusVisible),
      outlinedBorder: alpha(colors.neutral[0], colors.opacity.outlinedBorder),
    },
    whiteStates: {
      main: colors.neutral[900],
      hover: alpha(colors.neutral[900], colors.opacity.hover),
      selected: alpha(colors.neutral[900], colors.opacity.selected),
      focus: alpha(colors.neutral[900], colors.opacity.focus),
      focusVisible: alpha(colors.neutral[900], colors.opacity.focusVisible),
      outlinedBorder: alpha(colors.neutral[900], colors.opacity.outlinedBorder),
    },
  },
  background: {
    default: colors.neutral[1000],
    background1: colors.neutral[900],
    background2: colors.neutral[800],
    background3: colors.neutral[700],
    tooltip: colors.neutral[900],
  },
  border: colors.neutral[600],
  divider: colors.neutral[600],
  native: {
    scrollbarBg: colors.neutral[500],
  },
  focusRing: colors.focus[600],
  input: {
    background: colors.neutral[900],
    disabledBackground: colors.neutral[800],
    border: colors.neutral[600],
    hoverBorder: colors.primary[500],
    focusBorder: colors.primary[500],
    focusRing: colors.primary[800],
    placeholder: colors.neutral[500],
    default: colors.neutral[600],
    hover: colors.primary[500],
    focus: colors.primary[800],
  },
  switch: {
    thumb: colors.neutral[300],
    thumbDisabled: colors.neutral[600],
    track: alpha(colors.neutral[0], 0.38),
  },
  slider: {
    valueLabelBackground: alpha(colors.neutral[800], 0.9),
    valueLabelText: colors.neutral[0],
  },
  stepper: {
    connector: colors.neutral[600],
  },
  // Accent colors exposed for direct use
  amber: { main: colors.amber[400] },
  purple: { main: colors.purple[400] },
  teal: { main: colors.teal[400] },
  indigo: { main: colors.indigo[400] },
  lime: { main: colors.lime[400] },
  pink: { main: colors.pink[400] },
  deepPurple: { main: colors.deepPurple[400] },
  neutral: { main: colors.neutral[500] },
}

export type SemanticTokens = typeof lightTokens

// Accent colors keyed by name for direct access
// Extended palette with all shades needed by Avatar and Chip components:
// - lighter (50): Chip background
// - light (100): Avatar constituent background
// - main (600): Avatar staff background, constituent foreground
// - dark (700): Chip text/icon
// - darker (800): Chip delete icon hover
export const accentColors = {
  neutral: {
    lighter: colors.neutral[50],
    light: colors.neutral[100],
    mediumLight: colors.neutral[200],
    main: colors.neutral[600],
    dark: colors.neutral[700],
    darker: colors.neutral[800],
  },
  blue: {
    lighter: colors.blue[50],
    light: colors.blue[100],
    mediumLight: colors.blue[200],
    main: colors.blue[600],
    dark: colors.blue[700],
    darker: colors.blue[800],
  },
  green: {
    lighter: colors.green[50],
    light: colors.green[100],
    mediumLight: colors.green[200],
    main: colors.green[600],
    dark: colors.green[700],
    darker: colors.green[800],
  },
  red: {
    lighter: colors.red[50],
    light: colors.red[100],
    mediumLight: colors.red[200],
    main: colors.red[600],
    dark: colors.red[700],
    darker: colors.red[800],
  },
  orange: {
    lighter: colors.orange[50],
    light: colors.orange[100],
    mediumLight: colors.orange[200],
    main: colors.orange[600],
    dark: colors.orange[700],
    darker: colors.orange[800],
  },
  purple: {
    lighter: colors.purple[50],
    light: colors.purple[100],
    mediumLight: colors.purple[200],
    main: colors.purple[600],
    dark: colors.purple[700],
    darker: colors.purple[800],
  },
  teal: {
    lighter: colors.teal[50],
    light: colors.teal[100],
    mediumLight: colors.teal[200],
    main: colors.teal[600],
    dark: colors.teal[700],
    darker: colors.teal[800],
  },
  primary: {
    lighter: colors.primary[50],
    light: colors.primary[100],
    mediumLight: colors.primary[200],
    main: colors.primary[600],
    dark: colors.primary[700],
    darker: colors.primary[800],
  },
  pink: {
    lighter: colors.pink[50],
    light: colors.pink[100],
    mediumLight: colors.pink[200],
    main: colors.pink[600],
    dark: colors.pink[700],
    darker: colors.pink[800],
  },
  indigo: {
    lighter: colors.indigo[50],
    light: colors.indigo[100],
    mediumLight: colors.indigo[200],
    main: colors.indigo[600],
    dark: colors.indigo[700],
    darker: colors.indigo[800],
  },
  amber: {
    lighter: colors.amber[50],
    light: colors.amber[100],
    mediumLight: colors.amber[200],
    main: colors.amber[600],
    dark: colors.amber[700],
    darker: colors.amber[800],
  },
  lime: {
    lighter: colors.lime[50],
    light: colors.lime[100],
    mediumLight: colors.lime[200],
    main: colors.lime[600],
    dark: colors.lime[700],
    darker: colors.lime[800],
  },
  deepPurple: {
    lighter: colors.deepPurple[50],
    light: colors.deepPurple[100],
    mediumLight: colors.deepPurple[200],
    main: colors.deepPurple[600],
    dark: colors.deepPurple[700],
    darker: colors.deepPurple[800],
  },
  yellow: {
    lighter: colors.yellow[50],
    light: colors.yellow[100],
    mediumLight: colors.yellow[200],
    main: colors.yellow[600],
    dark: colors.yellow[700],
    darker: colors.yellow[800],
  },
  coral: {
    lighter: colors.coral[50],
    light: colors.coral[100],
    mediumLight: colors.coral[200],
    main: colors.coral[600],
    dark: colors.coral[700],
    darker: colors.coral[800],
  },
} as const

export type AccentColorName = keyof typeof accentColors
export type AccentColor = (typeof accentColors)[AccentColorName]

// Accent color palette as array for index-based access (backward compatibility)
export const accentPalette = Object.values(accentColors)

// Chart color palette — grouped by cool tones (blues → greens → teals → purples → pinks) then warm tones.
// Use chartColors[i] for the i-th data series. Use chartOtherColor for the aggregated "Other" bucket.
export const chartColors = [
  colors.blue[500],
  colors.coral[500],
  colors.teal[500],
  colors.purple[500],
  colors.amber[500],
  colors.indigo[400],
  colors.green[500],
  colors.pink[400],
  colors.lime[500],
  colors.violet[500],
  colors.red[500],
  colors.cyan[500],
  colors.neutral[400],
] as const

// Labels for chart colors (matches chartColors order)
export const chartColorLabels = [
  'Blue',
  'Coral',
  'Teal',
  'Purple',
  'Amber',
  'Indigo',
  'Green',
  'Pink',
  'Lime',
  'Violet',
  'Red',
  'Cyan',
  'Neutral',
] as const

// Neutral color reserved for the "Other" / aggregated bucket in charts
export const chartOtherColor = colors.neutral[400]
export const chartOtherLabel = 'Other' as const
